import React, { useState, useEffect } from "react";
import { signOut } from "aws-amplify/auth";
import { useAuth } from "../context/AuthProvider";
import API from "../service/API";
import { modal } from "../modal/ModalService";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";
import logoImage from "../img/granhand_logo.png";
import couponIcon from "../img/icon/coupon.png";
import coinIcon from "../img/icon/coin.png";

const REASON_OPTIONS = [
  {
    label: "원하는 상품이 없어요.",
    value: "item",
  },
  {
    label: "과도한 쇼핑을 자제하고 싶어요.",
    value: "expense",
  },
  {
    label: "쿠폰/포인트 등의 혜택이 너무 적어요.",
    value: "benefit",
  },
  {
    label: "다른 계정으로 가입하고 싶어요.",
    value: "change",
  },
  {
    label: "직접입력",
    value: "custom",
  },
];

const StepRender = React.memo(
  ({
    step,
    reason,
    setReason,
    customReason,
    setCustomReason,
    onNext,
    onPrev,
    busy,
    userInfo,
    agree,
    setAgree,
    i18n,
  }) => {
    switch (step) {
      case 1:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("dropout.step1.title")}
              </p>
            </div>

            <div className="signup-auth__form">
              <fieldset className="dropout-auth__reasons">
                <legend className="sr-only">{i18n("dropout.step1.title")}</legend>
                {REASON_OPTIONS.map((r) => (
                  <label key={r.value} className="dropout-auth__reason">
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="dropout-auth__reason-input"
                    />
                    <span className="dropout-auth__reason-icon" aria-hidden="true" />
                    <span className="dropout-auth__reason-label">
                      {i18n(`dropout.reason.${r.value}`)}
                    </span>
                  </label>
                ))}
              </fieldset>

              {reason === "custom" && (
                <div className="dropout-auth__custom">
                  <textarea
                    value={customReason}
                    onChange={(e) =>
                      setCustomReason(e.target.value.slice(0, 200))
                    }
                    rows={3}
                    placeholder={i18n("dropout.reason.custom.placeholder")}
                    aria-label={i18n("dropout.reason.custom.aria")}
                    className="dropout-auth__textarea"
                  />
                  <div className="dropout-auth__count">
                    {customReason.length}/{200}
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={onNext}
                disabled={!reason || (reason === "custom" && !customReason)}
                aria-disabled={!reason || (reason === "custom" && !customReason)}
                className="signup-auth__submit"
              >
                {i18n("common.next")}
              </button>
            </div>
          </section>
        );

      case 2:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("dropout.step2.title")}
              </p>
            </div>

            <div className="dropout-auth__benefits">
              <div className="dropout-auth__benefit">
                <img
                  alt={i18n("dropout.benefit.iconAlt")}
                  className="dropout-auth__benefit-icon"
                  src={couponIcon}
                />
                <span className="dropout-auth__benefit-label">
                  {i18n("dropout.benefit.coupon.line1")}
                </span>
                <span className="dropout-auth__benefit-value">
                  {userInfo.coupon}
                </span>
              </div>

              <div className="dropout-auth__benefit">
                <img
                  alt={i18n("dropout.benefit.iconAlt")}
                  className="dropout-auth__benefit-icon"
                  src={coinIcon}
                />
                <span className="dropout-auth__benefit-label">
                  {i18n("dropout.benefit.point.line1")}
                </span>
                <span className="dropout-auth__benefit-value">
                  {userInfo.point}
                </span>
              </div>
            </div>

            <div className="dropout-auth__actions">
              <button
                type="button"
                onClick={onPrev}
                className="signup-auth__outline-button"
              >
                {i18n("common.prev")}
              </button>
              <button
                type="button"
                onClick={onNext}
                className="signup-auth__submit"
              >
                {i18n("common.next")}
              </button>
            </div>
          </section>
        );

      case 3:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("dropout.step3.title")}
              </p>
            </div>

            <div className="signup-auth__step">
              <div className="signup-auth__summary">
                <p className="signup-auth__summary-title">
                  {i18n("dropout.notice.title")}
                </p>
              </div>

              <div className="dropout-auth__notice">
                <div className="dropout-auth__notice-item">
                  <p className="dropout-auth__notice-title">
                    {i18n("dropout.notice.rejoin.title")}
                  </p>
                  <p className="dropout-auth__notice-desc">
                    {i18n("dropout.notice.rejoin.desc")}
                  </p>
                </div>

                <div className="dropout-auth__notice-item">
                  <p className="dropout-auth__notice-title">
                    {i18n("dropout.notice.benefitLimit.title")}
                  </p>
                  <p className="dropout-auth__notice-desc">
                    {i18n("dropout.notice.benefitLimit.desc")}
                  </p>
                </div>

                <div className="dropout-auth__notice-item">
                  <p className="dropout-auth__notice-title">
                    {i18n("dropout.notice.idReuse.title")}
                  </p>
                  <p className="dropout-auth__notice-desc">
                    {i18n("dropout.notice.idReuse.desc")}
                  </p>
                </div>

                <div className="dropout-auth__notice-item">
                  <p className="dropout-auth__notice-title">
                    {i18n("dropout.notice.dataDeletion.title")}
                  </p>
                  <p className="dropout-auth__notice-desc">
                    {i18n("dropout.notice.dataDeletion.desc")}
                  </p>
                </div>

                <div className="dropout-auth__notice-item">
                  <p className="dropout-auth__notice-title">
                    {i18n("dropout.notice.posts.title")}
                  </p>
                  <p className="dropout-auth__notice-desc">
                    {i18n("dropout.notice.posts.desc")}
                  </p>
                </div>
              </div>

              <label className="dropout-auth__agree">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{i18n("dropout.notice.agree")}</span>
              </label>

              <div className="dropout-auth__actions">
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={busy}
                  aria-disabled={busy}
                  className="signup-auth__outline-button"
                >
                  {i18n("common.prev")}
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={!agree || busy}
                  aria-disabled={!agree || busy}
                  className="signup-auth__submit"
                >
                  {i18n("dropout.cta.submit")}
                </button>
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  }
);

export default function DropoutPage() {
  const { user, loading, refresh } = useAuth();
  const { i18n } = useLang();
  const [userInfo, setUserinfo] = useState({});
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState(null);
  const [customReason, setCustomReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  useEffect(() => {
    API.getUserInfo(user?.username)
      .then((data) => {
        setUserinfo(data);
      })
      .catch((err) => {
        console.error("getUserInfo API error:", err);
      });
  }, []);

  const handleBack = () => {
    window.location.replace("/my-page");
  };

  const handleNext = () => {
    if (step === 3) {
      doDelete();
    } else {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const doDelete = async () => {
    try {
      setBusy(true);
      await API.disableUser(user.username, reason, customReason);
      await signOut();
      await refresh();
    } catch (e) {
      await modal.alert({
        message: e?.message || String(e),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signup-auth dropout-auth container">
      <div className="signup-auth__frame">
        <div className="signup-auth__toolbar">
          <button
            type="button"
            className="signup-auth__back"
            onClick={handleBack}
            aria-label={i18n("signin.nav.back.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              {step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep")}
            </span>
          </button>
          <div className="signup-auth__lang">
            {step === 1 ? <LanguageDropdown /> : null}
          </div>
        </div>

        <div className="signup-auth__brand">
          <img alt="GRANHAND." src={logoImage} />
        </div>

        <section className="signup-auth__content">
        <StepRender
          step={step}
          reason={reason}
          customReason={customReason}
          setCustomReason={setCustomReason}
          setReason={setReason}
          onNext={handleNext}
          onPrev={handlePrev}
          busy={busy}
          userInfo={userInfo}
          agree={agree}
          setAgree={setAgree}
          i18n={i18n}
        />
        </section>
      </div>
    </main>
  );
}
